import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

export default function Product() {
  const [open, setOpen] = useState(null);
  const { t } = useLanguage();
  const p = t.product;

  return (
    <section id="product" className="py-24 md:py-32 lg:py-40 bg-ink text-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-aurora">{p.label}</p>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] border border-aurora text-aurora px-3 py-1">
              {p.badge}
            </span>
          </div>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            {p.titlePrefix}<span className="font-serif-accent italic font-medium text-aurora">{p.titleHighlight}</span>{p.titleSuffix}
          </h2>
          <p className="mt-8 max-w-2xl text-lg font-light text-paper/70 leading-relaxed">
            {p.description}
          </p>
        </Reveal>

        {/* Architecture flow */}
        <Reveal delay={0.1}>
          <div className="mt-20">
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-paper/50 mb-8">{p.archLabel}</p>
            <div className="flex flex-col items-start gap-3">
              {p.flow.map((node, i) => (
                <motion.div key={node} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="w-full">
                  <div className={`inline-block border ${i === 2 ? "border-aurora bg-aurora/10" : "border-paper/25"} px-6 py-4 font-display font-medium text-xl md:text-2xl`}>
                    {node}
                  </div>
                  {i < p.flow.length - 1 && <ArrowDown size={20} className="text-aurora my-1 ml-8" />}
                </motion.div>
              ))}
            </div>

            {/* Agents */}
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {p.agents.map((a, i) => (
                <button key={a.name} onClick={() => setOpen(open === i ? null : i)} className="text-left border border-paper/20 hover:border-aurora p-6 transition-colors duration-300" data-testid={`agent-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-display font-medium text-lg">{a.name}</span>
                    <ChevronDown size={18} className={`transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                  </div>
                  <AnimatePresence>
                    {open === i && (
                      <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden text-sm font-light text-paper/60 mt-3 leading-relaxed">
                        {a.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Features */}
        <Reveal delay={0.1}>
          <div className="mt-24">
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-paper/50 mb-8">{p.featuresLabel}</p>
            <div className="flex flex-wrap gap-3">
              {p.features.map((f) => (
                <span key={f} className="border border-paper/20 px-4 py-2 font-mono-tech text-xs text-paper/80">{f}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Tech */}
        <Reveal delay={0.1}>
          <div className="mt-24">
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-paper/50 mb-8">{p.techLabel}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {p.techStack.map((ts) => (
                <div key={ts.label} className="border border-paper/20 p-6 hover:-translate-y-1 transition-transform duration-300">
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-aurora mb-4">{ts.label}</p>
                  <ul className="space-y-1.5">
                    {ts.items.map((it) => (
                      <li key={it} className="font-display text-lg">{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Product Documentation removed per request */}
      </div>
    </section>
  );
}
